import { memo, useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Headphones, Mic2, Trash2, Edit3, Upload, Download, Circle, Lock, Unlock, Plug, Wand2, Sparkles, History } from "lucide-react";
import { toast } from "sonner";
import { tintFor, padCh, trimName } from "./dawHelpers";
import { useMicAliases } from "@/lib/useMicAliases";
import PluginManagerPanel from "@/components/PluginManagerPanel";
import LedLevelMeter from "@/components/suno/LedLevelMeter";

/**
 * Horizontal mixer row — one full-width row per channel.
 *
 * Layout (left → right):
 *   [Channel ID]  [Name]  [Mute/Solo/Arm]  [⏤────●───── Volume slider ─────]  [Pan]  [Edit]  [Load]  [Clear]
 *
 * The volume slider stretches across most of the row, so it's much more usable
 * than the tiny one inside the vertical ChannelStrip when you have 64 channels.
 *
 * Wrapped in React.memo + custom prop equality so only the rows whose peak/vol
 * actually changed rerender each animation tick. With 16 visible rows × 60 fps
 * this saves ~960 VDOM diffs/sec on the mixer alone.
 */
function MixerRow({
  index, channel, engine,
  // Primitive snapshots — see propsEqual below for why these are needed.
  hasBuffer, channelName, channelMuted, channelSolo,
  channelSourceUrl = null,
  pitchBendSemi, pluginCount: pluginCountProp,
  peak, externalVolume, selected, recordingIdx,
  onSelect, onUpload, onClear, onOpenClipEditor,
  armed, onToggleArm, sourceCh, onSourceChChange, inputChannelCount = 0,
  onRecordChannel,
  onOpenVocalCleanup,
  // Phase-3 collab channel lock — see useChannelLocks.
  lockedByMe = false, lockedByOther = false, lockHolder = null, onToggleLock = null,
  // iter-105 — drives the BPM-synced row pulse when the engine is playing
  // and this row has a buffer loaded.
  playing = false,
  // iter-198 — rolling live waveform samples (array of 0..1 peaks, length
  // ≤ 96). When defined and non-empty, a thin scrolling wave-strip
  // renders above the existing peak underline so the producer can
  // instantly see which channels are actually contributing to the mix
  // right now (vs which are dormant or silent).
  liveSamples = null,
  clipping = false,
  clippedRecently = false,
  liveWaveRev = 0,
  // iter-246 — number of saved takes for this channel + open-modal callback.
  takeCount = 0,
  onOpenTakeHistory = null,
}) {
  void liveWaveRev; // memo invalidation only
  const tint = tintFor(index);
  const [vol, setVol] = useState(() => Math.round(channel?.volume ?? 80));
  const [pan, setPan] = useState(() => Math.round((channel?.pan ?? 0) * 50));
  const [pitchBend, setPitchBend] = useState(() => channel?.pitchBendSemi ?? 0);
  const [muted, setMuted] = useState(!!channel?.muted);
  const [solo, setSolo] = useState(!!channel?.solo);
  // Plugin modal — per-row, opens the same PluginManagerPanel as the strips view.
  const [pluginOpen, setPluginOpen] = useState(false);
  // Prefer the primitive snapshot prop from MixerPanel so the memo can
  // invalidate when the count changes; fall back to live read for safety.
  const pluginCount = pluginCountProp ?? channel?.plugins?.length ?? 0;
  const fileRef = useRef(null);
  // iter-222 — per-input alias map. Drives dropdown labels so "Mic 7"
  // can show as "SM7B" / "Lead Vocal" once the user renames it from
  // the Routing Matrix modal. Hook returns a stable `getLabel` ref
  // backed by localStorage, no per-render cost.
  const { getLabel: getMicLabel } = useMicAliases();

  // Mirror engine state when it changes externally (e.g. from CRDT collab).
  useEffect(() => {
    const v = pitchBendSemi ?? channel?.pitchBendSemi ?? 0;
    if (v !== pitchBend) setPitchBend(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitchBendSemi, channel?.pitchBendSemi]);

  // External volume change (MIDI / preset) → reflect locally + animate.
  const [flashKey, setFlashKey] = useState(0);
  useEffect(() => {
    if (externalVolume != null && externalVolume !== vol) {
      setVol(Math.round(externalVolume));
      setFlashKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalVolume]);

  // Write-gate: when another peer has acquired the lock, swallow local
  // mutations + flash a toast. The CRDT path still applies their changes
  // — this just prevents US from racing them.
  const checkLock = () => {
    if (lockedByOther) {
      toast.info(`Channel locked`, {
        description: `${lockHolder?.name || "Someone"} has exclusive control of CH ${padCh(index)}.`,
        duration: 2200,
      });
      return false;
    }
    return true;
  };

  const handleVolChange = (e) => {
    if (!checkLock()) return;
    const next = Number(e.target.value);
    setVol(next);
    engine.setChannelVolume(index, next);
  };
  const handlePanChange = (e) => {
    if (!checkLock()) return;
    const next = Number(e.target.value);
    setPan(next);
    engine.setChannelPan(index, next / 50);
  };
  const handlePitchBendChange = (e) => {
    if (!checkLock()) return;
    const next = Number(e.target.value);
    setPitchBend(next);
    engine.setChannelPitchBend(index, next);
  };
  const handlePitchBendReset = (e) => {
    e?.stopPropagation();
    if (!checkLock()) return;
    setPitchBend(0);
    engine.setChannelPitchBend(index, 0);
  };
  const handleMute = () => {
    if (!checkLock()) return;
    const next = !muted;
    setMuted(next);
    engine.setChannelMute(index, next);
  };
  const handleSolo = () => {
    if (!checkLock()) return;
    const next = !solo;
    setSolo(next);
    engine.setChannelSolo(index, next);
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(index, file);
    e.target.value = "";
  };

  // Mini-waveform behind the slider — gives quick visual ID of the channel
  const wave = useMemo(
    () => channel?.waveform || Array.from({ length: 80 }, (_, i) => 0.04 + Math.abs(Math.sin((i + 1) * (index + 2) * 0.19)) * 0.18),
    [channel?.waveform, index]
  );

  return (
    <div
      data-testid={`mixer-row-${index}`}
      onClick={() => onSelect(index)}
      className={`relative mb-1.5 flex items-center gap-2 rounded-xl border px-2 py-1.5 transition cursor-pointer ${
        selected ? "border-fuchsia-400/50 bg-fuchsia-500/[0.04]" :
        "border-white/10 bg-white/[0.025] hover:bg-white/[0.05]"
      } ${hasBuffer ? "mixer-row-loaded" : ""} ${hasBuffer && playing ? "mixer-row-live" : ""}`}
    >
      {/* iter-77 — lineage-group color stripe (same semantics as
          ChannelStrip — a 2px vertical bar on the left edge for grouped
          channels so the user can see which rows mute/solo together). */}
      {channel?.groupColor ? (
        <span
          data-testid={`mixer-row-group-${index}`}
          aria-hidden
          className="pointer-events-none absolute inset-y-1.5 left-0 w-[2px] rounded-r"
          style={{ backgroundColor: channel.groupColor, boxShadow: `0 0 6px ${channel.groupColor}80` }}
          title={channel.groupId ? `Linked group: ${channel.groupId}` : "Linked group"}
        />
      ) : null}
      {/* Channel ID — gets a small pulsing LED when this channel has audio loaded. */}
      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md font-mono text-[10px] ${
        channel?.buffer ? `${tint.dot} mixer-led` : "bg-white/15"
      } text-black/80`}>
        {padCh(index)}
      </span>

      {/* Name */}
      <span className="w-28 flex-shrink-0 truncate font-mono text-[10px] text-white/70">
        {trimName(channel?.name, 14) || "—"}
      </span>

      {/* iter-219 — Cloud badge when the buffer was loaded from a public
          URL (Suno / SongLibrary / SunoModal). Tells the user this
          channel will hit the stem-extraction URL fast-path (skips the
          slow WAV upload). */}
      {channelSourceUrl && (
        <span
          data-testid={`mixer-row-cloud-${index}`}
          title="Loaded from URL — stem extraction skips the upload step"
          className="ml-1 flex-shrink-0 rounded-md bg-sky-500/20 px-1 py-[1px] font-mono text-[9px] text-sky-200 ring-1 ring-sky-300/40"
        >☁</span>
      )}

      {/* iter-246 — Take History badge. Renders only when this channel
          has at least one saved take in the in-memory ring buffer.
          Click → open the per-channel TakeHistoryModal where the user
          can revert / delete individual takes. */}
      {takeCount > 0 && onOpenTakeHistory ? (
        <button
          type="button"
          data-testid={`mixer-row-takes-${index}`}
          onClick={(e) => { e.stopPropagation(); onOpenTakeHistory(index); }}
          title={`${takeCount} previous take${takeCount === 1 ? "" : "s"} · click to revert`}
          className="ml-1 inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-[1px] font-mono text-[9px] text-emerald-200 ring-1 ring-emerald-400/40 transition hover:bg-emerald-500/30 hover:text-emerald-50"
        >
          <History className="h-2.5 w-2.5" />
          <span>{takeCount}</span>
        </button>
      ) : null}

      {/* iter-201 — lingering CLIPPED badge, 1.5 s after the flash window. */}
      {clippedRecently && (
        <span
          data-testid={`mixer-row-clipped-${index}`}
          title="This channel clipped recently — bring the fader down."
          className="ml-1 flex-shrink-0 rounded-full bg-red-500/90 px-1.5 py-[1px] font-mono text-[8.5px] font-bold uppercase tracking-wider text-white shadow-[0_0_6px_rgba(239,68,68,0.85)] animate-pulse"
        >Clip</span>
      )}

      {/* Phase-3 channel lock indicator — visible only inside a collab room.
          - No lock state OR onToggleLock null → renders nothing (solo mode).
          - Locked by me   → glowing emerald padlock; click releases.
          - Locked by other → peer-colored padlock with tooltip; click is no-op.
          - Unlocked       → faint hover-only unlock icon; click acquires.    */}
      {onToggleLock ? (
        <button
          type="button"
          data-testid={`mixer-row-lock-${index}`}
          aria-pressed={lockedByMe || lockedByOther}
          title={
            lockedByMe ? "You hold this channel — click to release"
            : lockedByOther ? `Locked by ${lockHolder?.name || "Someone"}`
            : "Acquire exclusive control"
          }
          onClick={(e) => {
            e.stopPropagation();
            if (lockedByOther) return;  // intentional no-op — UI shows the holder
            onToggleLock();
          }}
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition ${
            lockedByMe
              ? "bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-400/60 shadow-[0_0_10px_rgba(52,211,153,0.45)]"
              : lockedByOther
              ? "ring-1 ring-white/15"
              : "bg-white/[0.04] text-white/35 hover:bg-white/[0.1] hover:text-white/70"
          }`}
          style={lockedByOther && lockHolder?.color ? { background: lockHolder.color + "55", color: "#fff" } : undefined}
        >
          {lockedByMe || lockedByOther ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
        </button>
      ) : null}

      {/* Mute / Solo / Arm / Rec indicator */}
      <div className="flex flex-shrink-0 gap-1">
        <button
          data-testid={`mixer-row-mute-${index}`}
          onClick={(e) => { e.stopPropagation(); handleMute(); }}
          className={`h-6 w-6 rounded-md text-[9px] font-bold transition ${
            muted ? "bg-rose-500/30 text-rose-200 ring-1 ring-rose-400/50 mixer-mute-on" : "bg-white/[0.06] text-white/60 hover:bg-white/[0.12]"
          }`}
          title="Mute"
        >M</button>
        <button
          data-testid={`mixer-row-solo-${index}`}
          onClick={(e) => { e.stopPropagation(); handleSolo(); }}
          className={`h-6 w-6 rounded-md text-[9px] font-bold transition ${
            solo ? "bg-amber-500/30 text-amber-200 ring-1 ring-amber-400/50 mixer-solo-on" : "bg-white/[0.06] text-white/60 hover:bg-white/[0.12]"
          }`}
          title="Solo"
        >S</button>
        <button
          data-testid={`mixer-row-arm-${index}`}
          onClick={(e) => { e.stopPropagation(); onToggleArm?.(index); }}
          className={`h-6 w-6 rounded-md text-[9px] font-bold transition ${
            armed
              ? "bg-rose-500/40 text-white shadow-[0_0_8px_rgba(244,63,94,0.5)] ring-1 ring-rose-400/60 animate-pulse"
              : "bg-white/[0.06] text-white/60 hover:bg-rose-500/20 hover:text-rose-200"
          }`}
          title={armed ? "Disarm channel" : "Arm channel for recording"}
        >
          <Circle className={`mx-auto h-3 w-3 ${armed ? "fill-current" : ""}`} />
        </button>
        {/* One-click record on THIS channel — bypasses the arm flow. Toggles
            between start/stop. Distinct mic icon + emerald accent so users
            don't confuse it with the arm circle next to it. */}
        {onRecordChannel ? (
          <button
            data-testid={`mixer-row-rec-${index}`}
            onClick={(e) => { e.stopPropagation(); onRecordChannel(index); }}
            className={`flex h-6 w-6 items-center justify-center rounded-md transition ${
              recordingIdx === index
                ? "bg-rose-500 text-white shadow-[0_0_10px_rgba(244,63,94,0.7)] ring-1 ring-rose-300/60 animate-pulse"
                : "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40 hover:bg-emerald-500/40 hover:text-white"
            }`}
            title={recordingIdx === index ? "Stop recording" : "Record this channel (one click)"}
          >
            <Mic2 className="h-3 w-3" />
          </button>
        ) : null}
        {/* iter-221 — Input source dropdown. Pre-iter-221 this was
            gated on `armed && inputChannelCount > 1`, which forced the
            workflow: arm first → THEN pick the input. Users
            (correctly) wanted the opposite: see the input picker
            UP-FRONT so they can route Mic 7 to this track BEFORE
            pressing Arm. So we now show it whenever the interface
            reports 2+ inputs. Visual emphasis still escalates when
            armed (rose ring) so an active armed channel reads
            differently from an idle one. Label uses the user's
            preferred "Mic N" terminology. */}
        {inputChannelCount > 1 && (
          <select
            data-testid={`mixer-row-src-${index}`}
            value={sourceCh ?? 0}
            onChange={(e) => { e.stopPropagation(); onSourceChChange?.(index, Number(e.target.value)); }}
            onClick={(e) => e.stopPropagation()}
            title={armed
              ? "Which input feeds this DAW channel (live — change anytime)"
              : "Pick the input that will feed this channel when armed"}
            className={`h-6 cursor-pointer rounded-md bg-black/40 px-1 font-mono text-[9px] text-white focus:outline-none ring-1 ${
              armed
                ? "ring-rose-400/40 shadow-[0_0_6px_rgba(244,63,94,0.25)]"
                : "ring-white/15 hover:ring-emerald-400/40 hover:bg-emerald-500/10"
            }`}
          >
            {Array.from({ length: inputChannelCount }).map((_, i) => (
              <option key={`src-${index}-${i}`} value={i}>{getMicLabel(i)}</option>
            ))}
          </select>
        )}
      </div>

      {/* Volume — the big horizontal slider stretching across the row */}
      <div className="relative flex-1">
        <motion.div
          key={`flash-${flashKey}`}
          animate={flashKey > 0 ? {
            boxShadow: [
              `0 0 0 0 ${tint.glow || "rgba(255,255,255,0.0)"}`,
              `0 0 22px 6px ${tint.glow || "rgba(255,255,255,0.6)"}`,
              `0 0 0 0 ${tint.glow || "rgba(255,255,255,0.0)"}`,
            ],
          } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="rounded-lg"
        >
          {/* Background mini-waveform */}
          <div className="pointer-events-none absolute inset-0 flex items-center px-2 opacity-25">
            {wave.map((v, i) => (
              <span
                key={`row-${index}-bin-${i}`}
                className={`mx-[1px] w-[1px] rounded-full ${channel?.buffer ? tint.dot : "bg-white/30"}`}
                style={{ height: `${Math.max(4, v * 70)}%` }}
              />
            ))}
          </div>
          <input
            data-testid={`mixer-row-volume-${index}`}
            type="range" min="0" max="100" value={vol}
            onClick={(e) => e.stopPropagation()}
            onChange={handleVolChange}
            className="studio-fader relative w-full"
            style={{ accentColor: tint.accent }}
          />
        </motion.div>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none font-mono text-[9px] text-white/85"
          style={flashKey > 0 ? { color: tint.accent } : {}}
        >
          {vol}
        </span>
        {peak != null && (
          <span
            className="pointer-events-none absolute left-0 bottom-0 h-0.5 rounded-full bg-emerald-400/80 transition-all"
            style={{ width: `${Math.min(100, peak * 100)}%` }}
          />
        )}
        {/* iter-198 — Live wave-strip. 32 mini bars sampled at ~20 Hz from
            the channel's analyser; visible only when liveSamples is fresh.
            Sits ABOVE the peak underline as a thin 6px strip so it doesn't
            crowd the fader. Cyan = playback, rose = recording (color is
            decided by the caller by sending the appropriate samples). */}
        {liveSamples && liveSamples.length > 0 && (
          <div
            data-testid={`mixer-row-live-wave-${index}`}
            className="pointer-events-none absolute bottom-1 left-1 right-1 flex h-1.5 items-end gap-[1px]"
          >
            {liveSamples.slice(-32).map((v, i) => (
              <span
                key={`mr-lw-${index}-${i}`}
                className={`flex-1 rounded-full ${
                  clipping
                    ? "bg-red-400 shadow-[0_0_4px_rgba(248,113,113,0.95)]"
                    : recordingIdx === index
                      ? "bg-rose-300/95"
                      : "bg-cyan-300/85"
                }`}
                style={{ height: `${Math.max(15, v * 100)}%` }}
              />
            ))}
          </div>
        )}
      </div>

      {/* iter-255 — LED clip-indicator ladder. Reuses the
          LedLevelMeter shipped in iter-254 for the ELPM preview cards,
          driven here by the engine-computed `peak` value already
          flowing into this row. Lights up while the engine is playing
          OR while this channel is recording so producers see live
          input AND playback levels without two separate widgets. */}
      <div className="w-16 flex-shrink-0">
        <LedLevelMeter
          peak={peak ?? 0}
          playing={playing || recordingIdx === index || armed}
          orientation="horizontal"
          segments={10}
          testid={`mixer-row-led-${index}`}
          ariaLabel={`Level for CH ${padCh(index)}`}
        />
      </div>

      {/* Pan — compact */}
      <div className="w-20 flex-shrink-0">
        <input
          data-testid={`mixer-row-pan-${index}`}
          type="range" min="-50" max="50" value={pan}
          onClick={(e) => e.stopPropagation()}
          onChange={handlePanChange}
          className="w-full accent-white"
          title={pan === 0 ? "Center" : pan < 0 ? `L${Math.abs(pan)}` : `R${pan}`}
        />
        <span className="block text-center font-mono text-[9px] text-white/45">
          {pan === 0 ? "C" : pan < 0 ? `L${Math.abs(pan)}` : `R${pan}`}
        </span>
      </div>

      {/* Pitch Bend — semitones (-12..+12). Double-click the label to reset. */}
      <div className="w-24 flex-shrink-0">
        <input
          data-testid={`mixer-row-pitch-${index}`}
          type="range" min="-12" max="12" step="1" value={pitchBend}
          onClick={(e) => e.stopPropagation()}
          onChange={handlePitchBendChange}
          className="w-full accent-fuchsia-400"
          title={`Pitch bend ${pitchBend > 0 ? "+" : ""}${pitchBend} semitones`}
        />
        <button
          type="button"
          data-testid={`mixer-row-pitch-reset-${index}`}
          onDoubleClick={handlePitchBendReset}
          onClick={(e) => e.stopPropagation()}
          title="Double-click to reset"
          className="flex w-full items-center justify-center gap-0.5 font-mono text-[9px] text-fuchsia-200/80 hover:text-fuchsia-100"
        >
          <Wand2 className="h-2 w-2" />
          {pitchBend > 0 ? `+${pitchBend}` : pitchBend}st
        </button>
      </div>

      {/* Edit clip + Load + Clear */}
      <div className="flex flex-shrink-0 gap-1">
        <button
          data-testid={`mixer-row-edit-${index}`}
          onClick={(e) => { e.stopPropagation(); onSelect(index); onOpenClipEditor?.(); }}
          disabled={!channel?.buffer}
          className="grid h-6 w-6 place-items-center rounded-md bg-fuchsia-500/15 text-fuchsia-200 transition hover:bg-fuchsia-500/30 disabled:opacity-40"
          title="Edit clip (trim / split)"
        >
          <Edit3 className="h-3 w-3" />
        </button>
        <button
          data-testid={`mixer-row-plugin-${index}`}
          onClick={(e) => { e.stopPropagation(); setPluginOpen(true); }}
          className={`grid h-6 w-6 place-items-center rounded-md transition ${
            pluginCount > 0
              ? "bg-cyan-500/[0.22] text-cyan-100 ring-1 ring-cyan-400/40 hover:bg-cyan-500/[0.36]"
              : "bg-white/[0.06] text-white/70 hover:bg-cyan-500/[0.18] hover:text-cyan-100"
          }`}
          title={pluginCount > 0 ? `${pluginCount} plugin${pluginCount === 1 ? "" : "s"} loaded — manage` : "Load a synced AudioWorklet plugin"}
        >
          <Plug className="h-3 w-3" />
        </button>
        {/* iter-224 — Vocal Cleanup button. Opens the ElevenLabs Audio
            Isolation modal so the user can remove reverb / room echo /
            background noise from this channel. Disabled when the
            channel has no audio loaded yet. */}
        {onOpenVocalCleanup ? (
          <button
            data-testid={`mixer-row-cleanup-${index}`}
            onClick={(e) => { e.stopPropagation(); if (channel?.buffer) onOpenVocalCleanup(index); }}
            disabled={!channel?.buffer}
            className="grid h-6 w-6 place-items-center rounded-md bg-emerald-500/15 text-emerald-200 transition hover:bg-emerald-500/[0.35] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            title={channel?.buffer ? "Vocal Cleanup — remove reverb / room echo / noise" : "Load audio first"}
          >
            <Sparkles className="h-3 w-3" />
          </button>
        ) : null}
        <button
          data-testid={`mixer-row-load-${index}`}
          onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
          className="grid h-6 w-6 place-items-center rounded-md bg-white/[0.06] text-white/70 transition hover:bg-white/[0.12]"
          title="Load audio file"
        >
          <Upload className="h-3 w-3" />
        </button>
        <button
          data-testid={`mixer-row-export-${index}`}
          onClick={(e) => {
            e.stopPropagation();
            const blob = engine.channelToWavBlob(index);
            if (!blob) {
              toast.error("Nothing to export on this channel");
              return;
            }
            // Use the channel's name (or fallback) as the basename, ensure .wav
            const base = (channel?.name || `Channel-${padCh(index)}`).replace(/\.[^.]+$/, "");
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${base}.wav`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Revoke after the browser has had time to start the download.
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            toast.success(`Exported ${base}.wav`, { description: `${(blob.size / 1024).toFixed(0)} KB · 16-bit WAV` });
          }}
          disabled={!hasBuffer}
          className="grid h-6 w-6 place-items-center rounded-md bg-white/[0.06] text-white/70 transition hover:bg-sky-500/20 hover:text-sky-200 disabled:opacity-40"
          title={hasBuffer ? "Export this channel as WAV" : "Load audio to export"}
        >
          <Download className="h-3 w-3" />
        </button>
        <button
          data-testid={`mixer-row-clear-${index}`}
          onClick={(e) => { e.stopPropagation(); onClear(index); }}
          disabled={!channel?.buffer}
          className="grid h-6 w-6 place-items-center rounded-md bg-white/[0.06] text-white/70 transition hover:bg-rose-500/20 hover:text-rose-200 disabled:opacity-40"
          title="Clear channel"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      <input ref={fileRef} type="file" accept="audio/*,.mid,.midi" className="hidden" onChange={onFilePicked} />

      <PluginManagerPanel
        open={pluginOpen}
        onClose={() => setPluginOpen(false)}
        channelIndex={index}
        channel={channel}
        engine={engine}
      />
    </div>
  );
}

/** Custom prop-equality — peak and externalVolume tick every frame so we can't
 *  use the default referential check. Compare structural fields only.
 *  IMPORTANT: the engine mutates `channel.buffer` / `.name` / `.muted` etc. in
 *  place on the same channel object, so a referential `channel === channel`
 *  check is always true. We compare the inner fields that actually drive the
 *  UI (buffer presence, name, mute/solo) instead. */
function propsEqual(prev, next) {
  return (
    prev.index === next.index &&
    // Primitive snapshots — these DO invalidate the memo when the engine
    // mutates the underlying channel object in place. See MixerPanel.jsx
    // for why we don't compare `channel.buffer` / `channel.name` directly.
    prev.hasBuffer === next.hasBuffer &&
    prev.channelName === next.channelName &&
    prev.channelSourceUrl === next.channelSourceUrl &&
    prev.channelMuted === next.channelMuted &&
    prev.channelSolo === next.channelSolo &&
    prev.channelGroupColor === next.channelGroupColor &&
    prev.pitchBendSemi === next.pitchBendSemi &&
    prev.pluginCount === next.pluginCount &&
    prev.peak === next.peak &&
    prev.externalVolume === next.externalVolume &&
    prev.selected === next.selected &&
    prev.recordingIdx === next.recordingIdx &&
    prev.armed === next.armed &&
    prev.sourceCh === next.sourceCh &&
    prev.inputChannelCount === next.inputChannelCount &&
    // Phase-3 lock state — booleans + holder identity invalidate the memo
    // so the padlock badge re-renders the moment a peer claims/releases.
    // We also compare `!!onToggleLock` so the badge appears/disappears
    // when the user joins/leaves a collab room (the prop flips between a
    // function and null in that lifecycle).
    !!prev.onToggleLock === !!next.onToggleLock &&
    prev.lockedByMe === next.lockedByMe &&
    prev.lockedByOther === next.lockedByOther &&
    prev.lockHolder?.clientId === next.lockHolder?.clientId &&
    // iter-105 — invalidate the memo when transport play state flips so the
    // BPM-synced row pulse can start/stop in lockstep.
    prev.playing === next.playing &&
    // iter-198 — invalidate at ~20 Hz when this channel has live audio
    // so the mini wave-strip animates smoothly.
    prev.liveWaveRev === next.liveWaveRev &&
    // iter-200 — invalidate immediately on clip-flash transition.
    prev.clipping === next.clipping &&
    // iter-201 — same for the lingering 1.5 s clip badge.
    prev.clippedRecently === next.clippedRecently &&
    // iter-246 — invalidate when take history count changes so the
    // emerald "H N" badge updates immediately after a new capture.
    prev.takeCount === next.takeCount
    // Callbacks (onSelect/onUpload/etc.) are stable refs from the parent —
    // skip comparing them; otherwise this is a no-op memo.
  );
}

export default memo(MixerRow, propsEqual);
